/**
 * Local GAM (Google Publisher Tag) mock for Pick'em.
 *
 * GAM will not serve creatives to localhost, and it will not fill for a UK IP even on
 * prod — every anchored/footer ad fix has shipped unseen. This mock closes that gap:
 * it installs a `window.googletag` that satisfies the whole surface `src/utils/gpt.ts`
 * touches, so the REAL production code path — TenantContext → usePickemStickyFooterAd →
 * AdSlot → defineAdSlot/displayAdSlot/onSlotRenderEnded — runs unmodified, with the mock
 * standing in only for Google's ad server.
 *
 * Ported from `apps/squares/src/dev/gamMock.ts`; the two must stay behaviourally in step.
 * Differences here: Pick'em also uses page-level targeting (`pubads().setTargeting` /
 * `clearTargeting`), so the mock carries those.
 *
 * It reproduces the two GPT behaviours the footer bug hinged on:
 *
 *   1. Size-mapping bucket resolution — buckets sorted by viewport width descending,
 *      first bucket whose viewport fits the window wins; when a mapping exists but no
 *      bucket matches, GPT falls back to the slot's full `defineSlot` size list.
 *   2. The creative renders inside a fixed-size iframe, exactly as GPT injects it — so a
 *      creative taller than the frame GAM sized is clipped INSIDE the frame.
 *
 * ### Enabling it
 *
 * Add `?gamMock=1` to the app URL. The flag persists in sessionStorage so in-app
 * navigation keeps the mock; `?gamMock=0` turns it off. `index.html` skips loading the
 * real gpt.js when the flag is present, so the mock and Google's script never fight
 * over `googletag`.
 *
 * ### Tuning params (all optional)
 *
 *   - `gamMockSize=WxH`     — frame size GAM "chooses". Must be one of the sizes the
 *                             slot offered for the current viewport; an un-offered size
 *                             is refused with a console.warn.
 *   - `gamMockCreative=WxH` — size the CREATIVE is built at, independent of the frame.
 *   - `gamMockEmpty=1`      — no-fill: renders nothing, fires slotRenderEnded with
 *                             isEmpty=true.
 *   - `gamMockDelay=NN`     — ms between display() and the creative arriving
 *                             (default 400), so fill happens after mount like real GAM.
 */

import type { SlotRenderEndedEvent } from '@/utils/gpt';

const FLAG_PARAM = 'gamMock';
const STORAGE_KEY = 'pickem:gamMock';

export interface GamMockOptions {
  /** Frame size to force, when the slot offers it. */
  forcedFrameSize: [number, number] | null;
  /** Creative size to force inside the frame (defaults to the frame size). */
  forcedCreativeSize: [number, number] | null;
  /** Simulate GAM returning no ad. */
  empty: boolean;
  /** ms between display() and the creative arriving. */
  fillDelayMs: number;
}

/** The full event shape real GPT fires; `SlotRenderEndedEvent` is the narrowed view gpt.ts uses. */
interface MockSlotRenderEndedEvent extends SlotRenderEndedEvent {
  size: [number, number] | null;
  creativeId: number | null;
  lineItemId: number | null;
  advertiserId: number | null;
  campaignId: number | null;
  isBackfill: boolean;
}

const parseSize = (raw: string | null): [number, number] | null => {
  if (!raw) return null;
  const match = /^(\d+)x(\d+)$/i.exec(raw.trim());
  if (!match) return null;
  return [Number(match[1]), Number(match[2])];
};

export const readGamMockOptions = (search: string): GamMockOptions => {
  const params = new URLSearchParams(search);
  const rawDelay = params.get('gamMockDelay');
  const delay = rawDelay === null ? NaN : Number(rawDelay);
  return {
    forcedFrameSize: parseSize(params.get('gamMockSize')),
    forcedCreativeSize: parseSize(params.get('gamMockCreative')),
    empty: params.get('gamMockEmpty') === '1',
    fillDelayMs: Number.isFinite(delay) && delay >= 0 ? delay : 400,
  };
};

/**
 * Whether the mock is on for this session. The URL param is the switch and
 * sessionStorage is the memory, so the flag survives in-app URL changes but never
 * leaks between tabs or into a fresh session.
 */
export const isGamMockEnabled = (
  search: string = typeof window === 'undefined' ? '' : window.location.search
): boolean => {
  const value = new URLSearchParams(search).get(FLAG_PARAM);
  if (typeof sessionStorage !== 'undefined') {
    if (value === '1') sessionStorage.setItem(STORAGE_KEY, '1');
    if (value === '0') sessionStorage.removeItem(STORAGE_KEY);
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  }
  return value === '1';
};

interface SizeMappingBucket {
  viewport: [number, number];
  sizes: [number, number][];
}

interface MockSlot {
  adUnitPath: string;
  sizes: [number, number][];
  divId: string;
  targeting: Record<string, string>;
  sizeMapping: SizeMappingBucket[] | null;
  getSlotElementId: () => string;
  getAdUnitPath: () => string;
  getResponseInformation: () => Record<string, number | null> | null;
}

/**
 * GPT bucket resolution: buckets sorted by viewport width descending, the first whose
 * viewport fits the window wins. An empty `sizes` in the winning bucket means "serve
 * nothing here". No matching bucket falls back to the slot's full defineSlot list.
 */
export const resolveCandidateSizes = (
  slot: Pick<MockSlot, 'sizes' | 'sizeMapping'>,
  viewportWidth: number,
  viewportHeight: number
): [number, number][] => {
  if (!slot.sizeMapping || slot.sizeMapping.length === 0) return slot.sizes;

  const sorted = [...slot.sizeMapping].sort((a, b) => b.viewport[0] - a.viewport[0]);
  const bucket = sorted.find(
    b => b.viewport[0] <= viewportWidth && b.viewport[1] <= viewportHeight
  );
  return bucket ? bucket.sizes : slot.sizes;
};

/**
 * Which offered size the mock fills with: the forced one when offered, otherwise the
 * tallest (then widest) candidate — the worst case for clipping.
 */
export const pickFrameSize = (
  candidates: [number, number][],
  forced: [number, number] | null
): [number, number] | null => {
  if (candidates.length === 0) return null;
  if (forced) {
    const offered = candidates.find(([w, h]) => w === forced[0] && h === forced[1]);
    if (offered) return offered;
    console.warn(
      `[gamMock] gamMockSize=${forced[0]}x${forced[1]} is not among the sizes this slot ` +
        `offered for the current viewport (${candidates.map(s => s.join('x')).join(', ')}); ` +
        `GAM could never fill it. Falling back to the default pick.`
    );
  }
  return [...candidates].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0];
};

const creativeSrcdoc = (
  frame: [number, number],
  creative: [number, number],
  adUnitPath: string
): string => {
  const [cw, ch] = creative;
  // Full-bleed magenta creative with hard TOP/BOTTOM edge stripes so any clipping is
  // visible at a glance — if you cannot see both stripes, the creative is being cut.
  return `<!doctype html><html><head><style>
    html,body{margin:0;padding:0;overflow:hidden;background:transparent}
    .ad{width:${cw}px;height:${ch}px;box-sizing:border-box;position:relative;
      background:linear-gradient(90deg,#e20074,#a10053);
      font-family:Arial,Helvetica,sans-serif;color:#fff;
      display:flex;align-items:center;justify-content:center;flex-direction:column}
    .edge{position:absolute;left:0;right:0;height:6px;
      background:repeating-linear-gradient(90deg,#ffe600 0 12px,#111 12px 24px);
      color:#ffe600;font-size:8px;line-height:6px}
    .edge.top{top:0}.edge.bottom{bottom:0}
    .label{font-weight:bold;font-style:italic;font-size:${ch >= 90 ? 22 : 16}px;text-shadow:0 1px 2px rgba(0,0,0,.4)}
    .meta{font-size:10px;opacity:.85;margin-top:2px}
  </style></head><body>
    <div class="ad">
      <div class="edge top"></div>
      <div class="label">Game day is better — MOCK</div>
      <div class="meta">creative ${cw}x${ch} in frame ${frame[0]}x${frame[1]} · ${adUnitPath}</div>
      <div class="edge bottom"></div>
    </div>
  </body></html>`;
};

/**
 * Install the mock `window.googletag`. Call before anything queues GPT work; the cmd
 * queue executes immediately because the mock is born ready (`apiReady: true`).
 */
export const installGamMock = (
  options: GamMockOptions = readGamMockOptions(window.location.search)
): void => {
  const slots = new Map<string, MockSlot>();
  const renderListeners: ((event: MockSlotRenderEndedEvent) => void)[] = [];
  const pendingFills = new Map<string, ReturnType<typeof setTimeout>>();
  const lastResponse = new Map<string, Record<string, number | null> | null>();
  const pageTargeting: Record<string, string> = {};

  const fillSlot = (slot: MockSlot) => {
    const container = document.getElementById(slot.divId);
    if (!container) return;
    container.innerHTML = '';

    const candidates = resolveCandidateSizes(slot, window.innerWidth, window.innerHeight);
    const frame = options.empty ? null : pickFrameSize(candidates, options.forcedFrameSize);

    if (frame) {
      const creative = options.forcedCreativeSize ?? frame;
      const iframe = document.createElement('iframe');
      // Fixed-size frame exactly as GPT injects one: the creative inside cannot grow it.
      iframe.width = String(frame[0]);
      iframe.height = String(frame[1]);
      iframe.style.border = '0';
      iframe.style.display = 'block';
      iframe.setAttribute('scrolling', 'no');
      iframe.title = `mock ad ${slot.adUnitPath}`;
      iframe.srcdoc = creativeSrcdoc(frame, creative, slot.adUnitPath);
      container.appendChild(iframe);
    }

    console.info(
      `[gamMock] slotRenderEnded ${slot.divId} — ` +
        (frame ? `filled ${frame[0]}x${frame[1]}` : 'EMPTY (no fill)'),
      {
        targeting: slot.targeting,
        pageTargeting: { ...pageTargeting },
        adUnitPath: slot.adUnitPath,
      }
    );

    // Stable fake ids keyed to the served size — obviously not real GAM ids.
    const event: MockSlotRenderEndedEvent = {
      slot,
      isEmpty: !frame,
      size: frame,
      creativeId: frame ? Number(`9${frame[0]}${frame[1]}`) : null,
      lineItemId: frame ? Number(`8${frame[0]}${frame[1]}`) : null,
      advertiserId: frame ? 700000 : null,
      campaignId: frame ? 600000 : null,
      isBackfill: false,
    };
    lastResponse.set(
      slot.divId,
      frame
        ? {
            creativeId: event.creativeId,
            lineItemId: event.lineItemId,
            advertiserId: event.advertiserId,
            campaignId: event.campaignId,
          }
        : null
    );
    renderListeners.slice().forEach(listener => listener(event));
  };

  const scheduleFill = (divId: string) => {
    const slot = slots.get(divId);
    if (!slot) return;
    const pending = pendingFills.get(divId);
    if (pending) clearTimeout(pending);
    pendingFills.set(
      divId,
      setTimeout(() => {
        pendingFills.delete(divId);
        fillSlot(slot);
      }, options.fillDelayMs)
    );
  };

  const pubads = {
    enableSingleRequest: () => {},
    collapseEmptyDivs: () => {},
    getSlots: () => [...slots.values()],
    refresh: (toRefresh?: unknown[]) => {
      const targets = (toRefresh as MockSlot[] | undefined) ?? [...slots.values()];
      targets.forEach(slot => scheduleFill(slot.getSlotElementId()));
    },
    // Page-level targeting (Pick'em sets `s5` = game id on CompetitionView mount).
    setTargeting: (key: string, value: string | string[]) => {
      pageTargeting[key] = Array.isArray(value) ? value.join(',') : value;
      return pubads;
    },
    clearTargeting: (key?: string) => {
      if (key === undefined) Object.keys(pageTargeting).forEach(k => delete pageTargeting[k]);
      else delete pageTargeting[key];
      return pubads;
    },
    getTargeting: (key: string) => (key in pageTargeting ? [pageTargeting[key]] : []),
    getTargetingKeys: () => Object.keys(pageTargeting),
    addEventListener: (
      event: 'slotRenderEnded',
      listener: (e: MockSlotRenderEndedEvent) => void
    ) => {
      if (event === 'slotRenderEnded') renderListeners.push(listener);
      return pubads;
    },
    removeEventListener: (
      event: 'slotRenderEnded',
      listener: (e: MockSlotRenderEndedEvent) => void
    ) => {
      const index = renderListeners.indexOf(listener);
      if (event === 'slotRenderEnded' && index !== -1) renderListeners.splice(index, 1);
      return pubads;
    },
  };

  window.googletag = {
    apiReady: true,
    // Born ready: queued work runs synchronously, and anything pushed later too.
    cmd: {
      push: (fn: unknown) => {
        (fn as () => void)();
        return 0;
      },
    },
    defineSlot: (path: string, sizes: [number, number][], divId: string) => {
      const slot: MockSlot = {
        adUnitPath: path,
        sizes,
        divId,
        targeting: {},
        sizeMapping: null,
        getSlotElementId: () => divId,
        getAdUnitPath: () => path,
        getResponseInformation: () => lastResponse.get(divId) ?? null,
      };
      slots.set(divId, slot);
      const handle = {
        ...slot,
        addService: () => handle,
        defineSizeMapping: (mapping: unknown) => {
          slot.sizeMapping = mapping as SizeMappingBucket[];
          return handle;
        },
        setTargeting: (key: string, value: string) => {
          slot.targeting[key] = value;
          return handle;
        },
      };
      return handle;
    },
    pubads: () => pubads,
    // The builder just accumulates buckets; build() hands back the plain array the
    // mock's resolver understands.
    sizeMapping: () => {
      const buckets: SizeMappingBucket[] = [];
      const builder = {
        addSize: (viewport: [number, number], sizes: [number, number][]) => {
          buckets.push({ viewport, sizes });
          return builder;
        },
        build: () => buckets,
      };
      return builder;
    },
    enableServices: () => {},
    display: (divId: string) => scheduleFill(divId),
    destroySlots: (toDestroy?: unknown[]) => {
      const targets = (toDestroy as MockSlot[] | undefined) ?? [...slots.values()];
      targets.forEach(slot => {
        const divId = slot.getSlotElementId();
        const pending = pendingFills.get(divId);
        if (pending) clearTimeout(pending);
        pendingFills.delete(divId);
        slots.delete(divId);
        lastResponse.delete(divId);
        const container = document.getElementById(divId);
        if (container) container.innerHTML = '';
      });
      return true;
    },
  };

  console.info('[gamMock] installed — real gpt.js suppressed for this session', options);
};
